'use client';

/**
 * useUserStuckFunds Hook
 *
 * Scans all Hall of Shame protocols for user funds that may be stuck.
 * Uses multicall to efficiently batch read all user positions.
 *
 * Supports scanning for a friend's wallet by providing targetAddress.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useReadContracts, useChainId } from 'wagmi';
import { formatUnits, type Address, isAddress } from 'viem';
import type { HallOfShameProtocol, HallOfShameContract, UnderlyingAsset } from '@/lib/masterchef-cache/types';
import { useConnection } from 'wagmi';

// Debug logging helper
const DEBUG = true;
const log = (category: string, message: string, data?: any) => {
  if (!DEBUG) return;
  const timestamp = new Date().toISOString().split('T')[1].slice(0, -1);
  const prefix = `[useUserStuckFunds:${category}] ${timestamp}`;
  if (data !== undefined) {
    console.log(prefix, message, data);
  } else {
    console.log(prefix, message);
  }
};
interface StuckFundsPosition {
  protocol: {
    name: string;
    slug: string;
    status: string;
    statusReason?: string;
  };
  contract: {
    contractId: string;
    label: string;
    contractAddress: `0x${string}`;
    chainId: number;
  };
  poolId: number;
  lpTokenAddress: `0x${string}`;
  lpTokenSymbol?: string;
  lpTokenDecimals: number;
  stakedAmount: bigint;
  stakedAmountFormatted: string;
  pendingRewards?: bigint;
  underlyingAssets: UnderlyingAsset[];
  withdrawConfig: {
    standardWithdrawAbi: any;
    emergencyWithdrawAbi?: any;
    recommendation: 'standard' | 'emergency';
  };
}
interface UseUserStuckFundsReturn {
  positions: StuckFundsPosition[];
  isLoading: boolean;
  isScanning: boolean;
  error: Error | null;
  totalProtocolsScanned: number;
  protocolsWithFunds: number;
  scan: () => void;
  /** The address being scanned (either connected wallet or target address) */
  scannedAddress: Address | null;
  /** Whether we're scanning for someone else's wallet */
  isCheckingFriend: boolean;
  /** Whether at least one scan has been completed */
  hasScanned: boolean;
}
interface UseUserStuckFundsOptions {
  /** Optional address to check instead of connected wallet (for "check for a friend" feature) */
  targetAddress?: string;
}
export function useUserStuckFunds(hallOfShameProtocols: HallOfShameProtocol[], options: UseUserStuckFundsOptions = {}): UseUserStuckFundsReturn {
  const {
    targetAddress
  } = options;
  const {
    address: connectedAddress,
    isConnected
  } = useConnection();
  const chainId = useChainId();

  // Track render count for debugging
  const renderCount = useRef(0);
  renderCount.current++;
  log('init', `Render #${renderCount.current}`, {
    protocolCount: hallOfShameProtocols.length,
    targetAddress,
    connectedAddress,
    isConnected,
    chainId
  });

  // Determine which address to scan
  const addressToScan = useMemo(() => {
    if (targetAddress && isAddress(targetAddress)) {
      log('address', 'Using target address', targetAddress);
      return targetAddress as Address;
    }
    log('address', 'Using connected address', connectedAddress);
    return connectedAddress as Address | undefined;
  }, [targetAddress, connectedAddress]);
  const isCheckingFriend = !!(targetAddress && isAddress(targetAddress) && targetAddress.toLowerCase() !== connectedAddress?.toLowerCase());
  const [positions, setPositions] = useState<StuckFundsPosition[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [scanTrigger, setScanTrigger] = useState(0);
  const [hasScanned, setHasScanned] = useState(false);

  // Build multicall queries for all protocols, contracts, and pools
  const userInfoCalls = useMemo(() => {
    log('calls', 'Building calls...', {
      addressToScan,
      protocolCount: hallOfShameProtocols.length
    });
    if (!addressToScan || hallOfShameProtocols.length === 0) {
      log('calls', 'Skipping - no address or no protocols', {
        addressToScan,
        protocolCount: hallOfShameProtocols.length
      });
      return [];
    }
    const calls: Array<{
      address: Address;
      abi: any[];
      functionName: string;
      args: [bigint, Address];
      chainId: number;
      // Metadata for tracking
      _protocolIndex: number;
      _contractIndex: number;
      _poolId: number;
    }> = [];
    hallOfShameProtocols.forEach((protocol, protocolIndex) => {
      log('calls', `Processing protocol ${protocolIndex}: ${protocol.protocol.name}`, {
        contractCount: protocol.contracts.length
      });
      protocol.contracts.forEach((contractData, contractIndex) => {
        const userInfoAbi = contractData.recoveryConfig?.checkUserPosition?.userInfoAbi;
        if (!userInfoAbi) {
          log('calls', `WARNING: No userInfoAbi for contract ${contractIndex}`, {
            contractAddress: contractData.contract.contractAddress,
            recoveryConfig: contractData.recoveryConfig
          });
          return;
        }
        log('calls', `Contract ${contractIndex}: ${contractData.contract.contractAddress}`, {
          poolCount: contractData.pools.length,
          functionName: userInfoAbi.name
        });
        contractData.pools.forEach(pool => {
          calls.push({
            address: contractData.contract.contractAddress as Address,
            abi: [userInfoAbi],
            functionName: userInfoAbi.name,
            args: [BigInt(pool.poolId), addressToScan],
            chainId: contractData.contract.chainId,
            _protocolIndex: protocolIndex,
            _contractIndex: contractIndex,
            _poolId: pool.poolId
          });
        });
      });
    });
    log('calls', `Built ${calls.length} total calls`, {
      sampleCall: calls[0] ? {
        address: calls[0].address,
        functionName: calls[0].functionName,
        chainId: calls[0].chainId,
        poolId: calls[0]._poolId
      } : null
    });
    return calls;
  }, [addressToScan, hallOfShameProtocols]);

  // Execute multicall - allow scanning even without connected wallet if we have a target address
  const canScan = userInfoCalls.length > 0 && (isConnected || isCheckingFriend) && scanTrigger > 0;
  log('query', 'Query state', {
    canScan,
    callCount: userInfoCalls.length,
    isConnected,
    isCheckingFriend,
    scanTrigger
  });
  const {
    data: userInfoData,
    isLoading: isLoadingUserInfo,
    isError,
    error: queryError,
    refetch,
    status,
    fetchStatus
  } = useReadContracts({
    contracts: userInfoCalls.map(({
      _protocolIndex,
      _contractIndex,
      _poolId,
      ...call
    }) => call),
    query: {
      enabled: canScan
    }
  });

  // Log query state changes
  useEffect(() => {
    log('queryState', 'Query state changed', {
      status,
      fetchStatus,
      isLoading: isLoadingUserInfo,
      isError,
      error: queryError,
      hasData: !!userInfoData,
      dataLength: userInfoData?.length
    });
  }, [status, fetchStatus, isLoadingUserInfo, isError, queryError, userInfoData]);

  // Process results
  useEffect(() => {
    log('results', 'Processing effect triggered', {
      hasData: !!userInfoData,
      dataLength: userInfoData?.length,
      addressToScan,
      callCount: userInfoCalls.length
    });
    if (!userInfoData || !addressToScan) {
      log('results', 'Skipping - no data or no address');
      return;
    }
    log('results', `Processing ${userInfoData.length} results...`);
    const foundPositions: StuckFundsPosition[] = [];
    let successCount = 0;
    let errorCount = 0;
    let emptyCount = 0;
    userInfoData.forEach((result, index) => {
      if (result.status !== 'success') {
        errorCount++;
        if (index < 5) {
          // Only log first 5 errors to avoid spam
          log('results', `Call ${index} failed`, {
            status: result.status,
            error: result.error
          });
        }
        return;
      }
      if (!result.result) {
        emptyCount++;
        return;
      }
      successCount++;
      const call_0 = userInfoCalls[index];
      if (!call_0) {
        log('results', `WARNING: No call found for index ${index}`);
        return;
      }
      const protocol_0 = hallOfShameProtocols[call_0._protocolIndex];
      if (!protocol_0) {
        log('results', `WARNING: No protocol found for index ${call_0._protocolIndex}`);
        return;
      }
      const contractData_0 = protocol_0.contracts[call_0._contractIndex];
      if (!contractData_0) {
        log('results', `WARNING: No contract found for index ${call_0._contractIndex}`);
        return;
      }
      const pool_0 = contractData_0.pools.find(p => p.poolId === call_0._poolId);
      if (!pool_0) {
        log('results', `WARNING: No pool found for poolId ${call_0._poolId}`);
        return;
      }

      // Get staked amount from result
      const stakedAmountIndex = contractData_0.recoveryConfig.checkUserPosition.stakedAmountOutputIndex;
      const resultArray = result.result as unknown[];
      const stakedAmount = resultArray[stakedAmountIndex] as bigint;

      // Only include if user has funds
      if (stakedAmount > 0n) {
        const lpDecimals = pool_0.lpTokenDecimals ?? 18;
        log('results', `Found funds in pool ${pool_0.poolId}!`, {
          protocol: protocol_0.protocol.name,
          stakedAmount: stakedAmount.toString(),
          lpDecimals
        });
        foundPositions.push({
          protocol: {
            name: protocol_0.protocol.name,
            slug: protocol_0.protocol.slug,
            status: protocol_0.protocol.status,
            statusReason: protocol_0.protocol.statusReason
          },
          contract: {
            contractId: contractData_0.contract.contractId,
            label: contractData_0.contract.label,
            contractAddress: contractData_0.contract.contractAddress,
            chainId: contractData_0.contract.chainId
          },
          poolId: pool_0.poolId,
          lpTokenAddress: pool_0.lpTokenAddress,
          lpTokenSymbol: pool_0.lpTokenSymbol,
          lpTokenDecimals: lpDecimals,
          stakedAmount,
          stakedAmountFormatted: formatUnits(stakedAmount, lpDecimals),
          underlyingAssets: pool_0.underlyingAssets,
          withdrawConfig: {
            standardWithdrawAbi: contractData_0.recoveryConfig.withdraw.standardWithdraw,
            emergencyWithdrawAbi: contractData_0.recoveryConfig.withdraw.emergencyWithdraw,
            recommendation: contractData_0.recoveryConfig.withdraw.recommendation
          }
        });
      }
    });
    log('results', 'Processing complete', {
      successCount,
      errorCount,
      emptyCount,
      foundPositionsCount: foundPositions.length
    });
    setPositions(foundPositions);
    setIsScanning(false);
    setHasScanned(true);
    log('results', 'isScanning set to false, hasScanned set to true');
  }, [userInfoData, addressToScan, userInfoCalls, hallOfShameProtocols]);

  // Timeout ref for scan safety
  const scanTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scan = useCallback(() => {
    log('scan', 'Scan triggered', {
      addressToScan,
      protocolCount: hallOfShameProtocols.length,
      currentScanTrigger: scanTrigger
    });
    if (!addressToScan || hallOfShameProtocols.length === 0) {
      log('scan', 'Scan aborted - missing requirements', {
        addressToScan,
        protocolCount: hallOfShameProtocols.length
      });
      return;
    }
    log('scan', 'Starting scan...');
    setIsScanning(true);
    setError(null);
    setScanTrigger(t => {
      const newTrigger = t + 1;
      log('scan', `scanTrigger updated: ${t} -> ${newTrigger}`);
      return newTrigger;
    });

    // Safety timeout - reset isScanning after 30 seconds if still stuck
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }
    scanTimeoutRef.current = setTimeout(() => {
      log('scan', 'Scan timeout reached - resetting isScanning');
      setIsScanning(false);
      setHasScanned(true);
    }, 30000);

    // Trigger refetch
    log('scan', 'Calling refetch()...');
    refetch().then(result_0 => {
      log('scan', 'refetch() completed', {
        status: result_0.status,
        dataLength: result_0.data?.length,
        isError: result_0.isError,
        error: result_0.error
      });
      // Clear timeout since refetch completed
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
        scanTimeoutRef.current = null;
      }
      // If refetch succeeded but data processing effect won't run (no data change),
      // we still need to mark scan as complete
      if (result_0.status === 'success' && !result_0.data?.length) {
        log('scan', 'No data returned, marking scan complete');
        setPositions([]);
        setIsScanning(false);
        setHasScanned(true);
      }
    }).catch(err => {
      log('scan', 'refetch() error', err);
      setError(err);
      setIsScanning(false);
      setHasScanned(true);
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
        scanTimeoutRef.current = null;
      }
    });
  }, [addressToScan, hallOfShameProtocols, refetch, scanTrigger]);

  // Calculate stats
  const protocolsWithFunds = useMemo(() => {
    const uniqueProtocols = new Set(positions.map(p_0 => p_0.protocol.slug));
    return uniqueProtocols.size;
  }, [positions]);
  const contractsWithFunds = useMemo(() => {
    const uniqueContracts = new Set(positions.map(p_1 => p_1.contract.contractAddress));
    return uniqueContracts.size;
  }, [positions]);

  // Log return state periodically (not every render to avoid spam)
  useEffect(() => {
    log('return', 'Hook state summary', {
      positionsCount: positions.length,
      isLoading: isLoadingUserInfo,
      isScanning,
      hasError: !!error,
      totalProtocolsScanned: hallOfShameProtocols.length,
      protocolsWithFunds,
      scannedAddress: addressToScan,
      isCheckingFriend
    });
  }, [positions.length, isLoadingUserInfo, isScanning, error, hallOfShameProtocols.length, protocolsWithFunds, addressToScan, isCheckingFriend]);
  return {
    positions,
    isLoading: isLoadingUserInfo,
    isScanning,
    error,
    totalProtocolsScanned: hallOfShameProtocols.length,
    protocolsWithFunds,
    scan,
    scannedAddress: addressToScan || null,
    isCheckingFriend,
    hasScanned
  };
}